import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ClipboardEvent,
  type FunctionComponent,
  type KeyboardEvent,
} from 'react';
import { useIntl } from 'react-intl';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { dagMessages } from './messages';

const CODE_LENGTH = 8;
const VALID_CHAR = /^[A-Z0-9]$/;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const boxBase: CSSProperties = {
  width: '48px',
  height: '48px',
  textAlign: 'center',
  fontSize: '1.25rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  textTransform: 'uppercase',
  border: `2px solid ${token('color.border', '#ccc')}`,
  borderRadius: token('radius.medium', '0.375rem'),
  background: token('color.background.input', '#fff'),
  color: token('color.text', '#000'),
  outline: 'none',
  caretColor: 'transparent',
  transition: 'border-color 0.15s ease',
  boxSizing: 'border-box',
};

const boxFocused: CSSProperties = {
  ...boxBase,
  borderColor: token('color.border.focused', '#388BFF'),
};

const boxFilled: CSSProperties = {
  ...boxBase,
  borderColor: token('color.border.focused', '#388BFF'),
};

const separatorStyle: CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 300,
  color: token('color.text.subtlest', '#8993A5'),
  userSelect: 'none',
  paddingInline: token('space.050', '4px'),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DAG: FunctionComponent = () => {
  const { formatMessage } = useIntl();
  const [chars, setChars] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [showError, setShowError] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const userCodeRef = useRef('');
  const charsRef = useRef(chars);

  const focusAt = (index: number) => {
    const clamped = Math.max(0, Math.min(CODE_LENGTH - 1, index));
    inputRefs.current[clamped]?.focus();
  };

  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (chars[index]) {
        const next = [...chars];
        next[index] = '';
        setChars(next);
      } else {
        focusAt(index - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(index + 1);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      const next = [...chars];
      next[index] = '';
      setChars(next);
    }
  };

  const handleInput = (index: number) => (e: React.FormEvent<HTMLInputElement>) => {
    const raw = (e.currentTarget.value ?? '').toUpperCase();
    const typed = raw.replace(chars[index], '').slice(-1);
    if (!typed || !VALID_CHAR.test(typed)) {
      e.currentTarget.value = chars[index];
      return;
    }
    const next = [...chars];
    next[index] = typed;
    setChars(next);
    if (index < CODE_LENGTH - 1) {
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = [...chars];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setChars(next);
    focusAt(Math.min(pasted.length, CODE_LENGTH - 1));
  };

  const userCode = `${chars.slice(0, 4).join('')}-${chars.slice(4).join('')}`;
  userCodeRef.current = userCode;
  charsRef.current = chars;

  useEffect(() => {
    const form = wrapperRef.current?.closest('form');
    if (!form) return;
    const handleSubmit = (e: Event) => {
      if (userCodeRef.current.length < CODE_LENGTH) {
        e.preventDefault();
        setShowError(true);
        const firstEmpty = charsRef.current.findIndex((c) => !c);
        if (firstEmpty >= 0) inputRefs.current[firstEmpty]?.focus();
      }
    };
    form.addEventListener('submit', handleSubmit);
    return () => form.removeEventListener('submit', handleSubmit);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chars.includes('')) {
      setShowError(false);
    }
  }, [chars]);

  const boxStyle = (index: number): CSSProperties => {
    const hasError = showError && !chars[index];
    if (focusedIndex === index) return { ...boxFocused, ...(hasError ? { borderColor: token('color.border.danger', '#FF5630') } : {}) };
    if (chars[index]) return boxFilled;
    if (hasError) return { ...boxBase, borderColor: token('color.border.danger', '#FF5630') };
    return boxBase;
  };

  const renderBox = (index: number) => (
    <input
      key={index}
      ref={(el) => { inputRefs.current[index] = el; }}
      type="text"
      inputMode="text"
      maxLength={2}
      value={chars[index]}
      style={boxStyle(index)}
      onFocus={() => {
        setFocusedIndex(index);
        inputRefs.current[index]?.select();
      }}
      onBlur={() => setFocusedIndex(null)}
      onKeyDown={handleKeyDown(index)}
      onInput={handleInput(index)}
      onPaste={handlePaste}
      autoCapitalize="characters"
      autoComplete="off"
      spellCheck={false}
      aria-label={`Code character ${index + 1}`}
    />
  );

  return (
    <Box ref={wrapperRef} style={{ width: '100%', paddingInline: token('space.200'), paddingBlock: token('space.400') }}>
      <Stack space="space.300" alignInline="center">
        <Stack space="space.100" alignInline="center">
          <Text weight="bold" size="large">
            {formatMessage(dagMessages.userCodeLabel)}
          </Text>
          <Text as="p" size="medium" color="color.text.subtle" align="center">
            {formatMessage(dagMessages.userCodeDescription)}
          </Text>
        </Stack>

        <Stack space="space.100" alignInline="center">
          <Flex gap="space.100" alignItems="center" justifyContent="center">
            {Array.from({ length: 4 }, (_, i) => renderBox(i))}
            <span style={separatorStyle} aria-hidden>–</span>
            {Array.from({ length: 4 }, (_, i) => renderBox(i + 4))}
          </Flex>
          {showError && (
            <Text as="p" size="small" color="color.text.danger" align="center">
              {formatMessage(dagMessages.userCodeIncomplete)}
            </Text>
          )}
        </Stack>

        <input type="hidden" name="user_code" value={userCode} />

        <Text as="p" size="small" color="color.text.subtle" align="center">
          {formatMessage(dagMessages.antiphishingWarning)}
        </Text>
      </Stack>
    </Box>
  );
};

export default DAG;
